import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link2, Trash2 } from 'lucide-react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';

const DURATIONS = [2, 6, 12, 24, 48, 72];
const statsUrl = (token) => `${window.location.origin}/stats/${token}`;

/** Create, copy and revoke time-limited read-only stats links for organisers. */
const ShareStatsDialog = ({ open, onClose, eventId }) => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(6);
  const [label, setLabel] = useState('');

  const { data: links = [] } = useQuery({
    queryKey: ['statsLinks', eventId],
    queryFn: () => ApiService.getStatsLinks(eventId),
    enabled: open && !!eventId,
  });

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(statsUrl(token));
      showSuccess('Link copied');
    } catch (_) {
      showError(`Copy failed. Link: ${statsUrl(token)}`);
    }
  };

  const create = useMutation({
    mutationFn: () => ApiService.createStatsLink(eventId, hours, label),
    onSuccess: (link) => {
      queryClient.invalidateQueries({ queryKey: ['statsLinks', eventId] });
      setLabel('');
      copy(link.token);
    },
    onError: (err) => showError(err.message || 'Could not create the link'),
  });

  const revoke = useMutation({
    mutationFn: (token) => ApiService.revokeStatsLink(eventId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statsLinks', eventId] });
      showSuccess('Link revoked');
    },
    onError: (err) => showError(err.message || 'Could not revoke the link'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Share live stats</DialogTitle>
          <DialogDescription>
            Anyone with the link sees registrations, check-ins per purpose and tracked fields (no payment figures), refreshed every 30 seconds, until it expires.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
              <Input placeholder="Who is it for? (optional)" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
              <select
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
                aria-label="Valid for"
              >
                {DURATIONS.map((h) => <option key={h} value={h}>Valid {h} hours</option>)}
              </select>
              <Button onClick={() => create.mutate()} disabled={create.isPending}>
                <Link2 className="mr-2 h-4 w-4" />
                {create.isPending ? 'Creating…' : 'Create & copy'}
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">Active links</p>
              {links.length === 0 && <p className="text-sm text-gray-500">No active links.</p>}
              {links.map((link) => (
                <div key={link.token} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">{link.label || 'Stats link'}</p>
                    <p className="text-xs text-gray-500">Expires {formatDateTime(link.expiresAt)}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copy(link.token)}>
                    <Copy className="mr-1 h-4 w-4" />
                    Copy
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => revoke.mutate(link.token)} disabled={revoke.isPending} aria-label="Revoke link">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default ShareStatsDialog;
